import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfigService } from '@core/appconfig.service';
import { ProductService } from '@core/product.service';
import { UserService } from '@core/user.service';
import { ApiResponse } from '@models/api-response';
import { ILessonAssetLink } from '@models/lesson-asset-link';
import { LessonAssetLinkLaunchResult } from '@models/lesson-asset-link-launch-result';
import { ILessonStep } from '@models/lesson-section';
import { IProductVariant } from '@models/product-variant';
import { IViewerAsset, ViewerAsset } from '@models/viewer-asset';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ActivityService } from '@services/activity/activity.service';
import { ActivityResponse, ActivityStatus, ActivityUpdate } from '@services/activity/activity.service.model';
import { DrawingBrushSizes, DrawingColors, DrawingTools } from '@shared/drawing/drawing';
import { CompanyCode } from '@shared/enums/company-code';
import { LessonStepType } from '@shared/enums/lesson-type';
import { ProductType } from '@shared/enums/product-type';
import { RoleGroup } from '@shared/enums/role-type';
import { VariantType } from '@shared/enums/variant-type';
import { ViewerAssetType } from '@shared/enums/viewer-asset';
import { Helpers } from '@shared/helpers';
import { LoadingPlaceholderComponent } from '@shared/loading-placeholder/loading-placeholder.component';
import { MarkdownService } from '@shared/markdown.service';
import { UIHelpers } from '@shared/uihelpers';
import { ViewerConfigurationModel } from '@shared/viewer/viewer-configuration-model';
import { ViewerControlsComponent } from '@shared/viewer/viewer-controls/viewer-controls.component';

import { ViewerHelpers } from '@shared/viewer/viewer-helpers';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { skipWhile } from 'rxjs/operators';
import { LearningFacade } from '../../classrooms/store/learning.facade';
import { LessonService } from '../../products/teacher/lesson.service';
import { ViewerAudioComponent } from './viewer-audio/viewer-audio.component';
import { ViewerBackButtonComponent } from './viewer-back-button/viewer-back-button.component';
import { ViewerCanvasComponent } from './viewer-canvas/viewer-canvas.component';
import { ViewerDocumentComponent } from './viewer-document/viewer-document.component';
import { ViewerIFrameComponent } from './viewer-iframe/viewer-iframe.component';
import { ViewerPrintCanvasComponent } from './viewer-print-canvas/viewer-print-canvas.component';
import { ViewerVideoComponent } from './viewer-video/viewer-video.component';
import { ViewerVideoCanvasComponent } from './viewer-video-canvas/viewer-video-canvas.component';
import { ViewerService } from './viewer.service';

@Component({
  selector: 'zbp-viewer',
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.scss'],
  imports: [
    CommonModule,
    ViewerAudioComponent,
    ViewerCanvasComponent,
    ViewerDocumentComponent,
    ViewerIFrameComponent,
    ViewerPrintCanvasComponent,
    ViewerVideoComponent,
    ViewerVideoCanvasComponent,
    NgbDropdownModule,
    ViewerControlsComponent,
    LoadingPlaceholderComponent,
    ViewerBackButtonComponent,
  ],
})
export class ViewerComponent implements OnInit, OnDestroy {

  private subscriptions = new Subscription();

  /** 2 minutes in milliseconds */
  private readonly SUPER_SMART_COMPLETION_DURATION: number = 2 * 60 * 1000;

  @ViewChild('toggleDropdown') toggleDropdown: NgbDropdown;

  topicKey?: string;
  lessonTab?: string;
  productLineKey?: string;

  activeTool: DrawingTools = DrawingTools.none;
  drawingColor: DrawingColors = DrawingColors.black;
  brushSize: DrawingBrushSizes = DrawingBrushSizes.med;
  zoomPercent = 100;
  triggerPrint: boolean = false;

  isZBPortal: boolean = false;
  isLessonNavigation: boolean = false;

  shouldDisplayLoader$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  componentId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  assetId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  hasTopicKey$: Observable<boolean> = of(this.topicKey?.length > 0);
  clearClicked$ = new Subject<void>();

  readonly classroomId: WritableSignal<string> = signal<string>(null);

  assetList: ViewerAsset[] = [];

  viewerConfiguration: ViewerConfigurationModel;

  isInsideIframe: boolean = false;
  isStudent: boolean = false;
  isActivityComplete: boolean = false;
  isDesktop: boolean = false;

  /** Breakpoint matches CSS breakpoint between mobile & desktop. */
  private desktopQuery = matchMedia('(min-width: 768px)');

  viewerMessages$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  messages$: Observable<string[]> = this.viewerMessages$.asObservable();

  /** tracks the timer for supersmart completion */
  superSmartTimeout: ReturnType<typeof setTimeout> | undefined;

  toggleAssets$: Observable<ViewerAsset[]> = this.componentId$.pipe(
    filter(componentId => !!componentId),
    distinctUntilChanged(),
    switchMap(() => this.viewerService.getAssets()),
    map((res: ApiResponse<ViewerAsset[]>) => {
      if (res.success) {
        if (res.response[0]['viewerAssets']) {
          return res.response[0]['viewerAssets'];
        }
        // Filtered toggleAssets to not have daily routine type of viewer assets for now.
        // The new way of rendering daily routines from toggle dropdown will be worked during portal redesign.
        return res.response.filter(a => a.viewerAssetType !== ViewerAssetType.Dr);
      }
      this.viewerMessages$.next(res?.messages);
      return [];
    }),
    shareReplay(1)
  );

  viewerAssets$: Observable<ViewerAsset[]> = this.componentId$.pipe(
    filter(componentId => !!componentId),
    distinctUntilChanged(),
    switchMap(() => this.viewerService.getAssets(true)),
    map((res: ApiResponse<ViewerAsset[]>) => {
      if (res.success) {
        if (res.response[0]['viewerAssets']) {
          this.assetList = res.response[0]['viewerAssets'];
          return res.response[0]['viewerAssets'];
        }
        this.assetList = res.response;
        return res.response;
      }
      this.viewerMessages$.next(res?.messages);
      return [];
    }),
    shareReplay(),
  );

  viewerAsset$: Observable<ViewerAsset> = combineLatest([this.viewerAssets$, this.assetId$, this.componentId$]).pipe(
    map(([viewerAssets, assetId, componentId]) => this.selectViewerAsset(viewerAssets, assetId, componentId)),
    filter(viewerAsset => !!viewerAsset),
    distinctUntilChanged(),
    shareReplay(),
  );

  selectViewerAsset(viewerAssets: ViewerAsset[], assetId: string, componentId: string): ViewerAsset {
    const asset = viewerAssets.find(viewerAsset => viewerAsset.assetId === assetId
      && viewerAsset.viewerAssetType !== ViewerAssetType.Set
      // students ignore the componentId check
      && (this.isStudent || viewerAsset.componentId === componentId));
    const viewerAsset = {
      ...asset,
      category: asset?.category ? this.renderText(asset.category) : '',
      name: asset?.name ? this.renderText(asset.name) : '',
    };

    const navigationText = this.isLessonNavigation ? 'Lessons' : 'Materials';
    this.titleService.setTitle(`${navigationText} | ${asset?.category} - ${asset?.name} | ${this.appConfigService.getBrandTitle}`);
    this.liveAnnouncer.announce(`${viewerAsset.category} - ${viewerAsset.name}`, 'polite');
    return viewerAsset;
  }

  viewerConfig$: Observable<ViewerConfigurationModel | null> = this.viewerAsset$.pipe(
    // students do not show the viewer controls
    filter(assetId => !!assetId && !this.isStudent),
    map((asset) => {
      const config = this.viewerService.determineViewerConfiguration(asset as ViewerAsset);
      this.viewerConfiguration = config;
      return config;
    }),
    startWith(null),
    distinctUntilChanged(),
    shareReplay(),
  );


  /**
   * Gets the series for this viewer asset.
   *
   * @todo this should use ViewerService instead of duplicating effort, and split into separate
   *       pipes, `metadata$` and `series$` respectively.
   */
  series$: Observable<string> = this.componentId$.pipe(
    filter(componentId => !!componentId),
    map((componentId) => {
      const matches = componentId.match(Helpers.componentIdVariantPattern);
      const series = `${matches[1]}${matches[2]}`;
      if (matches && matches.length > 4) {
        if (series.substring(0, 3) === 'jiw') {
          return series.replace('jiw', 'jump');
        }
        if (series.substring(0, 5) === 'laesc') {
          return series.replace('laesc', 'hw');
        }
      }
      return series;
    }),
  );

  /**
   * @deprecated Exists to avoid rewriting old code, see {@link variantType$} for more robust grade/variant parsing.
   */
  grade$: Observable<string> = this.componentId$.pipe(
    filter(componentId => !!componentId),
    map((componentId) => {
      const matches = componentId.match(Helpers.componentIdVariantPattern);
      return matches[4].substring(0, 1) === 'g' ? matches[4].substring(1) : null;
    }),
    shareReplay(1),
  );

  /**
   * Use this, instead of {@link grade$}, for newer code.
   */
  variantType$: Observable<VariantType | null> = this.componentId$.pipe(
    filter(componentId => !!componentId),
    map(componentId => ViewerHelpers.mapComponentIdToVariantType(componentId)),
    shareReplay(1),
  );

  productType$: Observable<ProductType | null> = this.componentId$.pipe(
    filter(componentId => !!componentId),
    map(componentId => ViewerHelpers.getProductTypeByComponentId(componentId)),
    shareReplay(1),
  );

  /**
   * Activity is only used when viewing as a student.
   */
  activity$: Observable<ActivityResponse> = combineLatest([
    this.viewerAsset$,
    this.productType$,
    this.assetId$
  ]).pipe(
    // require truthy params
    filter(params => params.every(p => !!p)),
    switchMap(([viewerAsset, productType, assetId]) => {
      const query = {
        productType,
        activityKey: assetId,
        topicKey: this.topicKey,
      };
      if (this.isStudent) {
        switch (viewerAsset.viewerAssetType) {
        case ViewerAssetType.Video:
          return this.activityService.getVideoActivity(query);
        case ViewerAssetType.SuperSmart:
          return this.activityService.getSuperSmartActivity(query);
        case ViewerAssetType.Image:
          return this.activityService.getResourceActivity(query);
        default:
          break;
        }
      }
      return of(null);
    }),
    map((res) => {
      if (res?.response) {
        return res.response;
      }
      return null;
    }),
    shareReplay(1),
  );

  readonly lessonSteps$: Observable<ILessonStep[]> = combineLatest([
    this.productType$,
    this.variantType$,
  ]).pipe(
    skipWhile(() => !this.isLessonNavigation || !this.lessonTab),
    switchMap(([productType, variantType]) =>
      combineLatest([
        this.lessonService.getTopicLesson(productType, Helpers.mapVariantToGradeType(variantType), this.topicKey),
        this.learningFacade.getProductLineVariant(this.productLineKey, variantType),
        this.viewerAssets$,
        this.assetId$,
      ])
    ),
    map(([res, productVariant, viewerAssets, assetId]) => {
      const tab = res?.response?.tabs?.find(t => t.name === this.lessonTab)
      const stepCollection = tab?.section?.stepCollections?.find(c => c.type === LessonStepType.Step);
      const steps = stepCollection?.steps ?? [];
      return steps.map((step) => {
        return {
          ...step,
          assetLinks: step.assetLinks.map((link) => {
            const builtLaunchResult = this.buildAssetLinkUrl(link, productVariant, viewerAssets)
            let builtIsLocalViewerResult = null;
            let builtIsActiveAssetLink = false;
            if ('path' in builtLaunchResult) {
              builtIsLocalViewerResult = builtLaunchResult.path.startsWith(`${window.origin}/viewer`);
              builtIsActiveAssetLink = builtLaunchResult.path.includes(assetId);
            }
            return {
              ...link,
              builtLaunchResult,
              builtIsLocalViewerResult,
              builtIsActiveAssetLink,
            } satisfies ILessonAssetLink;
          })
        } satisfies ILessonStep
      });
    }),
    tap(() => this.cdr.detectChanges()),
    shareReplay(1)
  );

  private buildAssetLinkUrl(
    link: ILessonAssetLink,
    productVariant: IProductVariant,
    viewerAssets: IViewerAsset[]
  ): LessonAssetLinkLaunchResult {
    return this.productService.getLessonAssetLinkProductLaunchPath(
      link,
      () => this.topicKey,
      viewerAssets,
      this.productLineKey,
      productVariant,
      this.lessonTab,
    );
  }

  constructor(
    private viewerService: ViewerService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private markdown: MarkdownService,
    private userService: UserService,
    private appConfigService: AppConfigService,
    private titleService: Title,
    private liveAnnouncer: LiveAnnouncer,
    private activityService: ActivityService,
    private lessonService: LessonService,
    private productService: ProductService,
    private learningFacade: LearningFacade,
    private cdr: ChangeDetectorRef,
  ) {
    this.isInsideIframe = UIHelpers.isInsideIFrame();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.desktopQuery.removeEventListener('change', this.desktopListener);
    if (this.superSmartTimeout) {
      clearTimeout(this.superSmartTimeout);
    }
  }

  ngOnInit(): void {
    this.isDesktop = this.desktopQuery.matches;
    this.desktopQuery.addEventListener('change', this.desktopListener);
    this.isStudent = this.userService.user.isViewingAsRoleGroup(RoleGroup.students);
    this.isZBPortal = this.appConfigService.loginBrand$.value === CompanyCode.ZBPortal;
    this.subscriptions.add(
      // Configure properties for viewer based off query params.
      this.activatedRoute.queryParams?.subscribe((queryParams) => {
        this.topicKey = queryParams.topicKey;
        this.lessonTab = queryParams.lessonTab;
        this.productLineKey = queryParams.productLineKey;
        this.isLessonNavigation = queryParams.navigatedFrom === 'lesson';
        this.viewerService.language = queryParams.language;
        this.viewerService.topicKey = queryParams.topicKey;
        this.viewerService.componentId = queryParams.componentId;
        this.classroomId.set(queryParams.classroomId);
        this.assetId$.next(queryParams.assetId);
        this.componentId$.next(queryParams.componentId);
      })
    );
    this.subscriptions.add(this.activity$.subscribe());
  }

  handleToggledAsset(assetId: string): void {
    this.assetId$.next(assetId);
    this.router.navigate([], {
      queryParams: { assetId },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }


  setActiveTool(event: DrawingTools): void {
    this.activeTool = event;
  }

  setZoomPercent(event: number): void {
    this.zoomPercent = event;
  }

  clear() {
    this.clearClicked$.next();
  }

  setDrawingColor(color: DrawingColors) {
    this.drawingColor = color;
  }

  setBrushSize(size: DrawingBrushSizes) {
    this.brushSize = size;
  }

  printImage(): void {
    this.triggerPrint = true;
  }

  renderText(value: string): string {
    return this.markdown.renderInline(value);
  }

  handleError(errorMessage: any): void {
    this.viewerMessages$.next([errorMessage]);
    this.shouldDisplayLoader$.next(false);
  }

  async onImageLoaded() {
    if (this.isStudent) {
      const update = await this.getActivityCompleteUpdate();
      const response$ = this.activityService.updateResourceActivity(update);
      this.handleActivityResponse(response$);
    }
  }

  async onSuperSmartLoaded() {
    // only attempt to save if timeout is currently null
    if (this.isStudent && !this.superSmartTimeout) {
      this.superSmartTimeout = setTimeout(() => {
        this.onSuperSmartComplete();
        // reset timeout
        this.superSmartTimeout = null;
      }, this.SUPER_SMART_COMPLETION_DURATION);
    }
  }

  async onSuperSmartComplete() {
    if (this.isStudent) {
      const update = await this.getActivityCompleteUpdate();
      const response$ = this.activityService.updateSuperSmartActivity(update);
      this.handleActivityResponse(response$);
    }
  }

  async onVideoCompleted() {
    if (this.isStudent) {
      const update = await this.getActivityCompleteUpdate();
      const response$ = this.activityService.updateVideoActivity(update);
      this.handleActivityResponse(response$);
    }
  }

  private async getActivityCompleteUpdate(): Promise<ActivityUpdate> {
    const activity = await firstValueFrom(this.activity$);
    return {
      activityId: activity?.activityId ?? null,
      classroomId: activity?.classroomId ?? this.classroomId(),
      topicKey: activity?.topicKey ?? this.topicKey,
      activityKey: activity?.activityKey ?? await firstValueFrom(this.assetId$),
      sectionKey: activity?.sectionKey ?? null,
      productType: activity?.productType ?? await firstValueFrom(this.productType$),
      grade: activity?.grade ?? await firstValueFrom(this.variantType$),
      status: ActivityStatus.Complete,
    };
  }

  private handleActivityResponse(response$: Observable<ApiResponse<ActivityResponse>>): void {
    this.subscriptions.add(
      response$.pipe(
        map((res) => {
          if (!res?.response) {
            throw new Error('Response missing.');
          }
        })
      ).subscribe({
        next: () => {
          this.isActivityComplete = true;
        },
        error: (err) => {
          console.error('Unable to complete activity.', err);
        }
      })
    );
  }

  /**
   * Re-renders the viewer controls between mobile and desktop for proper keyboard navigation.
   *
   * CSS can't do this alone as certain asset types cannot be duplicated on the DOM (even when hidden by CSS).
   * Example: Wistia player needs an ID in order to attach to the page. The ID cannot be duplicated on the DOM.
   *
   * The controls will be re-rendered between desktop & mobile
   *  - allows the controls to render in the correct place in the DOM so users can tab to the controls.
   * Asset viewer stays the same
   *  - allows the state of viewer to persist
   */
  private desktopListener = (e: MediaQueryListEvent) => {
    this.isDesktop = e.matches;
  };
}
